import React, { useEffect, useState } from 'react';
import { Box, FormHelperText, IconButton, TextField, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { UploadRounded } from '@mui-symbols-material/w200';
import { DeleteRounded, DescriptionOutlined, ImageFilledRounded, ImageRounded } from '@mui-symbols-material/w300';
import { DropzoneArea } from '@dreebit-registry/vsm-microservice-components';
import { isImageFile } from '../../utils/fileType';
import {
    EventFilesValue,
    normalizeEventFilesValue,
    removeEventFile,
    renameEventFile,
    setEventFiles,
    toggleAssetImage,
} from '../../utils/eventFilesValue';

interface Props {
    attribute: { index: string };
    /** `EventFilesValue`; the legacy plain `File[]` is still accepted. */
    value: EventFilesValue | File[] | undefined;
    setFieldValue: (field: string, value: any) => void;
    /** ASSET_INSTALL only: lets the user mark one image as the asset's node image. */
    allowAssetImage?: boolean;
    /** Validation message from TDAForm (set when the step carries a `required` rule and no file was added). */
    error?: string;
    /** Replaces the default dropzone text. */
    dropzoneText?: string;
    /** Rendered above the dropzone, e.g. how to obtain the file to upload. */
    instructions?: React.ReactNode;
}

const Thumbnail = ({ file }: { file: File }) => {
    const [url, setUrl] = useState<string | null>(null);
    useEffect(() => {
        if (!file.type.startsWith('image/')) {
            setUrl(null);
            return;
        }
        const next = URL.createObjectURL(file);
        setUrl(next);
        return () => URL.revokeObjectURL(next);
    }, [file]);

    if (url) {
        return (
            <img
                src={url}
                alt={file.name}
                style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
            />
        );
    }
    return (
        <Box
            sx={{
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f4f4f4',
                borderRadius: '4px',
                flexShrink: 0,
            }}
        >
            <DescriptionOutlined sx={{ color: '#666' }} />
        </Box>
    );
};

const FilesDropzone = ({ attribute, value, setFieldValue, allowAssetImage, error, dropzoneText, instructions }: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const filesValue = normalizeEventFilesValue(value);
    const files = filesValue.files;
    // An empty list is stored as null: the library's `required` rule (Yup.mixed) only
    // rejects null/undefined, so an empty value object would pass it.
    const update = (next: EventFilesValue) =>
        setFieldValue(attribute.index, next.files.length > 0 ? next : null);

    // Re-key the underlying DropzoneArea after rename/remove so its internal state
    // re-syncs from `initialFiles` (formik's File[]). Pure adds don't bump the key
    // since onChange already keeps formik aligned with the dropzone's internal list.
    const [dropzoneKey, setDropzoneKey] = useState(0);

    // Local edit buffer keyed by file slot. Flushed to formik on blur.
    const [editingNames, setEditingNames] = useState<Record<number, string>>({});

    const handleRename = (index: number, newName: string) => {
        const original = files[index];
        const trimmed = newName.trim();
        if (!original || trimmed === '' || trimmed === original.name) return;
        const renamed = new File([original], trimmed, {
            type: original.type,
            lastModified: original.lastModified,
        });
        update(renameEventFile(filesValue, index, renamed));
        setDropzoneKey((k) => k + 1);
    };

    const handleRemove = (index: number) => {
        update(removeEventFile(filesValue, index));
        setDropzoneKey((k) => k + 1);
        setEditingNames((prev) => {
            const next = { ...prev };
            delete next[index];
            return next;
        });
    };

    return (
        <Box sx={{ width: '100%' }}>
            {instructions}
            <Box
                sx={{
                    '& .asset-file-management-file-upload-dropzone': {
                        aspectRatio: 'auto !important',
                        height: '180px !important',
                        ...(error && { borderColor: `${theme.palette.error.main} !important` }),
                    },
                }}
            >
                <DropzoneArea
                    key={dropzoneKey}
                    theme={theme}
                    fileObjects={[]}
                    initialFiles={files}
                    onChange={(loadedFiles: File[]) => {
                        update(setEventFiles(filesValue, loadedFiles));
                    }}
                    onDelete={() => {}}
                    filesLimit={10}
                    maxFileSize={999999999}
                    showPreviewsInDropzone={false}
                    showFileNames={false}
                    dropzoneText={dropzoneText ?? t('Click here or drag files to this area to upload them for this event.')}
                    dropzoneClass={'asset-file-management-file-upload-dropzone'}
                    dropzoneParagraphClass={'custom-file-upload-textbox'}
                    showAlerts={['error']}
                    Icon={UploadRounded}
                    classes={{
                        text: 'asset-file-management-dropzone-text',
                        root: 'dropzone-root',
                        icon: 'asset-file-management-dropzone-icon',
                    }}
                />
            </Box>
            {error && <FormHelperText error data-testid="event-files-error">{error}</FormHelperText>}

            {files.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {files.map((file, index) => {
                        const displayValue = editingNames[index] ?? file.name;
                        const isAssetImage = filesValue.assetImageIndex === index;
                        return (
                            <Box
                                key={`${index}-${file.name}-${file.size}`}
                                data-testid="event-file-row"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    border: '1px solid #eee',
                                    borderRadius: '5px',
                                    px: 1,
                                    py: 0.5,
                                }}
                            >
                                <Thumbnail file={file} />
                                <TextField
                                    fullWidth
                                    size="small"
                                    variant="standard"
                                    value={displayValue}
                                    onChange={(e) =>
                                        setEditingNames((prev) => ({ ...prev, [index]: e.target.value }))
                                    }
                                    onBlur={() => {
                                        handleRename(index, displayValue);
                                        setEditingNames((prev) => {
                                            const next = { ...prev };
                                            delete next[index];
                                            return next;
                                        });
                                    }}
                                />
                                {/* Exclusive marker: exactly one image can become the asset image. */}
                                {allowAssetImage && isImageFile(file.type) && (
                                    <Tooltip title={isAssetImage ? t('Remove as asset image') : t('Use as asset image')}>
                                        <IconButton
                                            size="small"
                                            color={isAssetImage ? 'primary' : 'default'}
                                            aria-pressed={isAssetImage}
                                            data-testid="event-file-asset-image-toggle"
                                            onClick={() => update(toggleAssetImage(filesValue, index))}
                                        >
                                            {isAssetImage
                                                ? <ImageFilledRounded sx={{ fontSize: 18 }} />
                                                : <ImageRounded sx={{ fontSize: 18 }} />}
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <IconButton size="small" onClick={() => handleRemove(index)}>
                                    <DeleteRounded sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

export default FilesDropzone;
